/**
 * Pure editing of `InternalUser.permissions`, which holds two historical shapes: a bare array
 * (legacy) or `{grant, revoke}`. Must never drop the half of the override the UI does not surface.
 */

export interface PermissionOverride {
    grant: string[];
    revoke: string[];
}

/** Permissions the staff editor exposes as checkboxes. */
export const GRANTABLE_PERMISSIONS = [
    {
        key: 'dashboard:access',
        label: 'Admin dashboard login',
        help: 'Lets this person sign into THIS dashboard with their username/password. Only Super Admin has it by default — credentials alone are never enough.',
    },
    {
        key: 'staff:propose',
        label: 'Add staff from the Manager App',
        help: 'Lets this person propose new agents in the Manager App. Anyone with their own reports can already do this; a Super Admin must still approve every proposal.',
    },
    {
        key: 'drivers:enroll',
        label: 'Enroll drivers',
        help: 'Lets this staff member look up and enroll drivers in the Manager App. Nobody below Super Admin has this until you tick it.',
    },
    {
        key: 'drivers:approve',
        label: 'Approve drivers (one press)',
        help: 'Their enroll button also finalises approval when photo, RC and DL are already verified — the same rule as this dashboard. Web verification roles (Region/City Manager, Verification Ops) hold this by role already.',
    },
    {
        key: 'rides:book_on_behalf',
        label: 'Book rides on behalf of riders',
        help: 'Lets this agent create a ride in the Manager App for a walk-up rider who has no app. Nobody below Super Admin has this until you tick it.',
    },
    {
        key: 'rides:cancel_on_behalf',
        label: 'Cancel rides they booked',
        help: 'Lets this agent cancel a booking they created. Has no effect without the booking permission.',
    },
    {
        key: 'fraud:review',
        label: 'Review potential-fraud rides',
        help: 'Clear or confirm rides the nightly fraud review flagged.',
    },
    {
        key: 'fraud:view_team',
        label: 'See team fraud alerts (Manager App)',
        help: "Shows a read-only \"Fraud alerts\" screen in the Manager App with the flagged rides of this person's team, never their own bookings. Clear / Confirm stays on this dashboard.",
    },
    // Support v2 Phase 5 — the Manager App support desk (plan §7.1). Nobody gets these for having a team.
    {
        key: 'support:handle',
        label: 'Handle support tickets (Manager App)',
        help: 'Support menu in the Manager App: their own and unassigned tickets, reply, status, notes, escalate, transfer to a queue. Support Agents have it by default.',
    },
    {
        key: 'support:lead',
        label: 'Support team lead (Manager App)',
        help: 'All tickets and queues, give tickets to other agents, approvals up to the compensation cap, QA reviews and team stats.',
    },
    {
        key: 'sos:respond',
        label: 'Respond to SOS (Manager App)',
        help: 'SOS console in the Manager App and LOUD SOS alerts on their phone (on duty first, everyone after 2 minutes).',
    },
    {
        key: 'drivers:suspend_inquiry',
        label: 'Suspend drivers pending inquiry',
        help: 'Can record the suspend decision on zero-tolerance / identity tickets (logged; an admin lifts suspensions).',
    },
] as const;

/**
 * UI mirror of the backend's ROLE_PERMISSIONS for the checkbox keys above. RE-AMENDED 2026-08-16
 * (second ruling): FIELD_AGENT holds NOTHING by default — every capability is a grant. The web
 * verification roles keep their pre-existing drivers:approve. Convenience only — the backend
 * remains the authority; this just lets the checkboxes show the EFFECTIVE state.
 */
export const ROLE_DEFAULT_PERMISSIONS: Record<string, string[]> = {
    SUPER_ADMIN: ['dashboard:access', 'staff:propose', 'drivers:enroll', 'drivers:approve', 'rides:book_on_behalf', 'rides:cancel_on_behalf', 'fraud:review', 'fraud:view_team', 'support:handle', 'support:lead', 'sos:respond', 'drivers:suspend_inquiry'],
    SUPPORT_AGENT: ['support:handle'],
    REGION_MANAGER: ['drivers:approve'],
    CITY_MANAGER: ['drivers:approve'],
    VERIFICATION_OPS: ['drivers:approve'],
};

const roleDefaults = (role?: string): string[] => ROLE_DEFAULT_PERMISSIONS[role ?? ''] ?? [];

/** The checkbox state: role default, then override — revoke beats grant beats default. */
export const isEffectivelyEnabled = (role: string | undefined, raw: unknown, permission: string): boolean => {
    const { grant, revoke } = normalizeOverride(raw);
    if (revoke.includes(permission)) return false;
    if (grant.includes(permission)) return true;
    return roleDefaults(role).includes(permission);
};

/**
 * Write the override that makes `permission` effectively on/off for this role:
 * defaults are switched OFF via a revoke entry (and back on by clearing it); non-defaults are
 * switched ON via a grant entry (and off by clearing it). Redundant entries are tidied so the
 * stored JSON stays the minimal intent.
 */
export const setEffective = (
    role: string | undefined,
    raw: unknown,
    permission: string,
    on: boolean,
): PermissionOverride => {
    const { grant, revoke } = normalizeOverride(raw);
    const isDefault = roleDefaults(role).includes(permission);
    if (on) {
        return {
            grant: isDefault ? grant.filter((p) => p !== permission) : uniq([...grant, permission]),
            revoke: revoke.filter((p) => p !== permission),
        };
    }
    return {
        grant: grant.filter((p) => p !== permission),
        revoke: isDefault ? uniq([...revoke, permission]) : revoke.filter((p) => p !== permission),
    };
};

const uniq = (xs: string[]): string[] => Array.from(new Set(xs));

/** Read any stored shape into a predictable { grant, revoke }. Unknown/malformed input reads as empty. */
export const normalizeOverride = (raw: unknown): PermissionOverride => {
    if (Array.isArray(raw)) {
        return { grant: uniq(raw.filter((p): p is string => typeof p === 'string')), revoke: [] };
    }
    if (raw && typeof raw === 'object') {
        const o = raw as { grant?: unknown; revoke?: unknown };
        return {
            grant: Array.isArray(o.grant) ? uniq(o.grant.filter((p): p is string => typeof p === 'string')) : [],
            revoke: Array.isArray(o.revoke) ? uniq(o.revoke.filter((p): p is string => typeof p === 'string')) : [],
        };
    }
    return { grant: [], revoke: [] };
};

/** Whether the override explicitly grants `permission` (role defaults are NOT considered here). */
export const hasGrant = (raw: unknown, permission: string): boolean =>
    normalizeOverride(raw).grant.includes(permission);

/**
 * Toggle a grant, preserving all others. ON also clears a matching revoke (revoke wins server-side).
 * ALWAYS returns an object: `undefined` vanishes in JSON, so the last uncheck never persisted.
 */
export const toggleGrant = (
    raw: unknown,
    permission: string,
    on: boolean,
): PermissionOverride => {
    const { grant, revoke } = normalizeOverride(raw);
    const nextGrant = on
        ? uniq([...grant, permission])
        : grant.filter((p) => p !== permission);
    const nextRevoke = on ? revoke.filter((p) => p !== permission) : revoke;

    return { grant: nextGrant, revoke: nextRevoke };
};
